import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, Wrench, Shield, Package, Sparkles, Instagram, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const Servicos = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const whatsappNumber = "5531971823249";
  const whatsappMessage = encodeURIComponent("Olá! Gostaria de solicitar um orçamento para os serviços da WellTec.");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+5531971823249';
  };

  // Scroll para o topo quando a página carregar
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const detailedServices = [
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Manutenção Preventiva",
      description: "Um conjunto de ações planejadas e periódicas (por tempo, uso ou ciclo) para inspecionar, limpar, lubrificar e substituir peças em equipamentos antes que ocorram falhas, visando prolongar sua vida útil, reduzir custos com reparos urgentes e garantir a eficiência operacional.",
      image: "https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/89504040-5631-4e92-8182-2ba872e69c51/8c33526e-468b-4c22-a33f-5c559480934b.webp",
      benefits: [
        "Prolonga a vida útil dos equipamentos",
        "Reduz custos com reparos urgentes",
        "Garante eficiência operacional",
        "Evita paradas inesperadas"
      ]
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Manutenção Corretiva",
      description: "Reparo especializado quando seu equipamento apresenta problemas ou falhas. Nossa equipe técnica identifica rapidamente o problema e executa o conserto com peças originais.",
      image: "https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/emh7ts1h_IMG_20260320_105016.jpg",
      benefits: [
        "Atendimento rápido",
        "Diagnóstico preciso",
        "Peças originais",
        "Garantia dos serviços"
      ]
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: "Limpeza Pós-Obra",
      description: "A limpeza pós-obra com lavadoras de piso é ideal para remover sujeiras pesadas e resíduos de cimento, tinta e pó de forma rápida e eficiente, pois lavam e secam simultaneamente, sem encharcar ou exigir interdição da área.",
      image: "https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/3f03e397-b93f-4164-919f-2c0bba197385/ab61f675-0696-4b82-9e90-94e36521d3bf.webp",
      benefits: [
        "Remove sujeiras pesadas",
        "Lava e seca simultaneamente",
        "Não encharça o ambiente",
        "Sem necessidade de interdição"
      ]
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Retrofitting (Reforma Completa)",
      description: "Uma reforma completa permite reduzir custos, restaurar o desempenho do equipamento, aumentar sua vida útil e diminuir falhas operacionais.",
      image: "https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/9445da0e-8012-4dc4-a8d4-e722da8b1580/Antes.webp",
      imageAfter: "https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/7cb775e8-3029-495c-9d95-c61c9e63f14d/dpois.webp",
      benefits: [
        "Reduz custos operacionais",
        "Restaura o desempenho",
        "Aumenta a vida útil",
        "Diminui falhas"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        {...seoData["/servicos"]}
        ogImage="https://www.wellttec.com/og-image.jpg"
      />
      
      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
        <Button
          onClick={handleWhatsAppClick}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/equipamentos-disponiveis" className="hover:text-blue-400 transition-colors">Catálogo</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a 
              href="https://www.instagram.com/welltecbrasil/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
              title="Instagram @welltecbrasil"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Início
              </button>
              <button
                onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Serviços
              </button>
              <button
                onClick={() => { navigate('/equipamentos-disponiveis'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Catálogo
              </button>
              <button
                onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400">
                Limpeza Pós-Obra
              </button>
              <button
                onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Locação
              </button>
              <button
                onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Blog
              </button>
              <a 
                href="/#contato" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors block">
                Contato
              </a>
              <a
                href="https://www.instagram.com/welltecbrasil/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors flex items-center">
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </a>
              <Button 
                onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }}
                className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Nossos Serviços</h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            A WellTec é especializada em soluções técnicas para manutenção e suporte de equipamentos de limpeza profissional, atendendo empresas de diversos segmentos com eficiência, confiabilidade e alto padrão técnico.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              Serviços Completos para Seus Equipamentos
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-lg text-gray-700 mb-6">
                Oferecemos serviços completos para garantir o melhor desempenho e a maior vida útil dos seus equipamentos:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 mt-1">✓</span>
                  <span>Manutenção preventiva e corretiva de lavadoras e varredeiras de piso</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 mt-1">✓</span>
                  <span>Reforma completa de equipamentos, reduzindo custos e aumentando a durabilidade</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 mt-1">✓</span>
                  <span>Locação de equipamentos de limpeza, com máquinas revisadas e prontas para operação</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 mt-1">✓</span>
                  <span>Venda de peças e equipamentos seminovos, com garantia e procedência</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 mt-1">✓</span>
                  <span>Atendimento técnico especializado, com profissionais experientes e suporte ágil</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 mt-6 font-semibold text-center">
                A WellTec trabalha com foco em qualidade, transparência e resultado, oferecendo soluções sob medida para cada cliente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="space-y-20">
            {detailedServices.map((service) => (
              <div key={service.title} className={`flex flex-col ${detailedServices.indexOf(service) % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center max-w-6xl mx-auto`}>
                {/* Image */}
                <div className="flex-1">
                  {service.image && (
                    <div className="space-y-4">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="rounded-lg shadow-xl w-full"
                      />
                      {service.imageAfter && (
                        <>
                          <p className="text-center text-sm font-semibold text-gray-600">Antes vs Depois</p>
                          <img 
                            src={service.imageAfter} 
                            alt={`${service.title} - Depois`}
                            className="rounded-lg shadow-xl w-full"
                          />
                        </>
                      )}
                    </div>
                  )}
                  {!service.image && (
                    <div className="bg-blue-100 rounded-lg p-12 flex items-center justify-center">
                      <div className="text-blue-600">
                        {service.icon}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="bg-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6 text-white">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-900 mb-3">Benefícios:</h4>
                    {service.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            Precisa de Manutenção ou Locação?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Entre em contato agora e descubra como nossos serviços podem otimizar suas operações
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-500 hover:bg-green-600 text-white text-lg px-12 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
            <Button 
              onClick={handleCallClick}
              size="lg"
              variant="outline"
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-12 py-6"
            >
              <Phone className="w-5 h-5 mr-2" />
              Ligar Agora
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                  alt="WellTec Logo" 
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Especialistas em locação e manutenção de lavadoras de piso industriais.
              </p>
              <a 
                href="https://www.instagram.com/welltecbrasil/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
                <span>@welltecbrasil</span>
              </a>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Links Rápidos</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="text-gray-400 hover:text-white transition-colors">Serviços</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="text-gray-400 hover:text-white transition-colors">Locação de Lavadora</Link></li>
                <li><Link to="/faq-locacao-lavadora-piso" className="text-gray-400 hover:text-white transition-colors">FAQ Locação</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
                <li></li>
                <li><a href="/#contato" className="text-gray-400 hover:text-white transition-colors">Contato</a></li>
                <li><a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Instagram</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Contato</h5>
              <ul className="space-y-2 text-gray-400">
                <li>(31) 97182-3249</li>
                <li>Mário Campos, MG</li>
                <li>Seg-Sex: 08:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Servicos;
